import { useState } from 'react';
import { useNavigate } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Pagination, Tag, TextInput } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { InlineError, PageHeader } from '../components/states';
import { Money, When } from '../components/format';
import { CommissionSwitch } from '../components/CommissionSwitch';
import { MaGiuCho } from '../components/MaDaiLy';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';

type Partner = {
  id: string; code: string; displayName: string; status: string; commissionRateBps: number;
  isSuperRoot: boolean; createdAt: string; parent?: { code: string } | null;
  balance?: { balance: string; vestingBalance: string } | null;
};
type Page = { items: Partner[]; total: number; page: number; pageSize: number };

const ST: Record<string, string> = { ACTIVE: 'Hoạt động', PAUSED: 'Tạm dừng', BANNED: 'Khóa' };

const FIELDS: FilterField[] = [
  { key: 'search', label: 'Tìm đại lý', kind: 'text', placeholder: 'Mã hoặc tên đại lý, mã giới thiệu' },
  { key: 'status', label: 'Trạng thái', kind: 'select', options: [['', 'Tất cả'], ...Object.entries(ST)] },
];
const KEYS = FIELDS.map((x) => x.key);

const columns: ColumnDef<Partner, any>[] = [
  { header: 'Mã', accessorKey: 'code', cell: (c) => <span className="num">{c.getValue()}</span> },
  { header: 'Tên', accessorKey: 'displayName' },
  { header: 'Tuyến trên', id: 'p', cell: (c) => c.row.original.parent?.code ?? (c.row.original.isSuperRoot ? 'gốc' : '-') },
  { header: 'Tỷ lệ (bps)', accessorKey: 'commissionRateBps', cell: (c) => <span className="num">{c.getValue()}</span> },
  { header: 'Số dư', id: 'bal', cell: (c) => <Money v={c.row.original.balance?.balance} /> },
  { header: 'Đang chờ', id: 'vest', cell: (c) => <Money v={c.row.original.balance?.vestingBalance} /> },
  { header: 'Trạng thái', accessorKey: 'status', cell: (c) => <Tag size="sm" type={c.getValue() === 'ACTIVE' ? 'green' : 'red'}>{ST[c.getValue()] ?? c.getValue()}</Tag> },
  { header: 'Tạo lúc', accessorKey: 'createdAt', cell: (c) => <When v={c.getValue()} /> },
];

export function AgentsPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const f = useUrlFilters(KEYS);
  const [open, setOpen] = useState(false);
  const [uid, setUid] = useState('');
  const [rate, setRate] = useState('80');
  const [code, setCode] = useState('');
  const [parentCode, setParentCode] = useState('');
  const q = useQuery({
    queryKey: ['agents', f.query, f.page, f.pageSize],
    queryFn: () => api.get<Page>('/api/admin/agents', { ...f.query, page: f.page, pageSize: f.pageSize }),
    placeholderData: keepPreviousData,
  });
  const activate = useMutation({
    mutationFn: () => api.post<{ partner: Partner }>('/api/admin/agents', {
      gameUserId: Number(uid), rateBps: Number(rate), ...(code ? { code } : {}), ...(parentCode ? { parentCode } : {}),
    }),
    onSuccess: (r) => {
      setOpen(false); setUid(''); setCode(''); setParentCode('');
      qc.invalidateQueries({ queryKey: ['agents'] }); nav('/agents/' + r.partner.id);
    },
  });
  return (
    <div className="page">
      <PageHeader title="Đại lý" lead="Cây đại lý, tỷ lệ Rolling và số dư hoa hồng. Portal riêng của đại lý do đội khác làm, gọi API /api/agent/*."
        actions={can('agents.manage') ? <Button size="sm" onClick={() => setOpen(true)}>Kích đại lý</Button> : undefined} />
      <FilterBar id="ag" fields={FIELDS} filters={f} exportPath="/api/admin/agents" />
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading || q.isPlaceholderData} error={q.error}
        onRetry={() => q.refetch()} onRowClick={(r) => nav('/agents/' + r.id)} rowKey={(r) => r.id}
        empty={f.active ? { title: 'Không có đại lý khớp bộ lọc', body: 'Nới bộ lọc để xem thêm.' } : { title: 'Chưa có đại lý', body: 'Kích đại lý từ người chơi hoặc nút phía trên.' }} />
      {q.data && q.data.total > 0 ? (
        <Pagination page={f.page} pageSize={f.pageSize} pageSizes={[20, 50, 100]} totalItems={q.data.total}
          itemsPerPageText="Mỗi trang" pageRangeText={(_c, t) => `trên ${t} trang`}
          itemRangeText={(min, max, total) => `${min} đến ${max} trong ${total.toLocaleString('vi-VN')} đại lý`}
          onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}
      <MaGiuCho />
      <CommissionSwitch />
      <Modal open={open} modalHeading="Kích đại lý" primaryButtonText="Kích" secondaryButtonText="Hủy"
        primaryButtonDisabled={!uid || activate.isPending} onRequestClose={() => setOpen(false)} onRequestSubmit={() => activate.mutate()}>
        <div className="stack">
          <TextInput id="ag-uid" labelText="Mã người chơi (gameUserId)" value={uid} onChange={(e) => setUid(e.target.value.replace(/\D/g, ''))} />
          <TextInput id="ag-code" labelText="Mã đại lý (tùy chọn)" helperText="3 đến 12 chữ số. Để trống: hệ thống tự cấp." value={code}
            maxLength={12} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          <TextInput id="ag-parent" labelText="Mã đại lý tuyến trên (tùy chọn)" helperText="Để trống: dưới nhà cái." value={parentCode}
            maxLength={12} onChange={(e) => setParentCode(e.target.value.replace(/\D/g, ''))} />
          <TextInput id="ag-rate" labelText="Tỷ lệ Rolling (bps)" helperText="Không vượt tỷ lệ của tuyến trên và mức trần nhà cái." value={rate}
            onChange={(e) => setRate(e.target.value.replace(/\D/g, ''))} />
          <InlineError error={activate.error} />
        </div>
      </Modal>
    </div>
  );
}
